import {
  Box,
  Button,
  Container,
  createStyles,
  Dialog,
  Divider,
  IconButton,
  makeStyles,
  Slide,
  Theme,
  Typography,
  withStyles
} from "@material-ui/core";
import React, {useCallback} from "react";
import {TransitionProps} from "@material-ui/core/transitions";
import MuiDialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import CloseIcon from '@material-ui/icons/Close';
import {DialogTitleProps} from "../../../creator/experienceManager/participantsDashboard";
import MuiDialogContent from "@material-ui/core/DialogContent/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions/DialogActions";
import {useDispatch, useSelector} from "react-redux";
import {
  getListParticipants,
  initListDetailData,
  removeParticipantsFromList
} from "../../../../../store/actions/participant/list";

const useStyles = makeStyles((theme) => ({
  paperWrap: {
    margin: 'auto',
    maxWidth: 500,
    borderRadius: 14,
  },
  description: {
    fontFamily: 'roboto-regular',
    fontSize: 14,
    color: '#697683'
  },
  emailText: {
    fontFamily: 'roboto-bold',
    color: '#212D40',
    fontSize: 18,
    textAlign: 'left'
  },
  emailWrap: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  actionDiv: {
    textAlign: 'right',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  cancelBtn: {
    backgroundColor: '#F0F0F0',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    boxShadow: 'none',
    textTransform: 'none'
  },
  deleteBtn: {
    backgroundColor: '#ED564A',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: 'white',
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#ED4F59'
    }
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const styles = (theme: Theme) =>
  createStyles({
    tagmodalheader: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    closeButton: {
      color: theme.palette.grey[500],
      fontFamily: 'roboto-bold',
    },
    closeBtnIcon: {
      color: '#212D4080',
      width: 24,
      height: 24,
      fontFamily: 'roboto-bold'
    },
    modaltitle: {
      fontFamily: 'Spartan-bold',
      fontSize: 24,
      color: '#212D40',
      textAlign: 'left'
    }
  });

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.tagmodalheader} {...other}>
      <Typography variant="h5" className={classes.modaltitle}>{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon classes={{ root: classes.closeBtnIcon }} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

interface IProps {
  listId: string;
  open: boolean;
  selectedParticipant: any;
  selectedParticipants: any[];
  onClose: () => void;
  onClearSelected: () => void;
}

const RemoveParticipantsModal = (props: IProps) => {
  const classes = useStyles();
  const { listId, open, selectedParticipants, selectedParticipant, onClose, onClearSelected } = props;
  const dispatch = useDispatch();
  const apolloClient = useSelector((state: any) => state.apolloClient);

  const onRemoveParticipantsFromList = useCallback(() => {
    !selectedParticipant && onClearSelected();

    const participant_ids = selectedParticipant
      ? [selectedParticipant.id]
      : selectedParticipants.map((p: any) => p.id);

    const request = {
      participants_id: participant_ids
    };
    dispatch(removeParticipantsFromList(apolloClient.client, request, (res: boolean) => {
      if (res) {
        dispatch(initListDetailData());
        setTimeout(() => {
          const req = {
            page: 0,
            group_id: Number(listId)
          };
          dispatch(getListParticipants(apolloClient.client, req, false));
        }, 1000);
      }
    }));
    onClose();
  }, [selectedParticipant, selectedParticipants, listId]);

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      maxWidth={"md"}
      classes={{
        paper: classes.paperWrap
      }}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle id="alert-dialog-slide-title" onClose={() => onClose()}>
        {selectedParticipant ? 'Remove Participant?' : (selectedParticipants.length > 1 ? 'Remove Participants?' : 'Remove Participant?')}
      </DialogTitle>
      <Divider />
      <DialogContent style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Container maxWidth="md">
          <Typography className={classes.description}>
            Are you sure you want to remove&nbsp;
            { selectedParticipant ? 'this participant' : (selectedParticipants.length > 1 ? 'these participants' : 'this participant') } from List? <br /> This operation cannot be undone.
          </Typography>
          <Box className={classes.emailWrap}>
            {selectedParticipant ? (
              <Typography variant="h6" className={classes.emailText}>
                {selectedParticipant.email}
              </Typography>
            ) : (
              selectedParticipants.map((participant: any, index: number) => (
                <Typography key={`email-list-label-${index}`} variant="h6" className={classes.emailText}>
                  {participant.email}
                </Typography>
              ))
            )}
          </Box>
        </Container>
      </DialogContent>
      <DialogActions>
        <Container maxWidth="md" className={classes.actionDiv}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Box mr={2}>
              <Button
                onClick={() => onClose()}
                variant="contained"
                className={classes.cancelBtn}
              >
                Cancel
              </Button>
            </Box>
            <Button
              onClick={onRemoveParticipantsFromList}
              variant="contained"
              color="secondary"
              className={classes.deleteBtn}
            >
              <span>Remove</span>
            </Button>
          </Box>
        </Container>
      </DialogActions>
    </Dialog>
  )
};

export default RemoveParticipantsModal;